import {
  CUSTOM_FIELD_SLOT,
  parseRupees,
  type CustomFieldValue,
  type CustomFieldValueInput,
  type ResolvedCustomField,
} from '@tailoring-crm/shared';

/**
 * Converting a record's stored custom values to and from what a form's inputs hold.
 *
 * Money is rupees on screen and paise on the wire, a `date` input holds `YYYY-MM-DD` while the
 * contract holds an instant, and a checkbox is `'yes'` or `''` because a form state of strings is
 * one state to reason about.
 */

/** The stored values, keyed by field key, as strings the inputs can hold. */
export function customValuesToFormState(
  stored: readonly CustomFieldValue[],
  fields: readonly ResolvedCustomField[],
): Record<string, string> {
  const state: Record<string, string> = {};
  for (const field of fields) {
    if (field.origin !== 'custom' || field.type === null) continue;
    const held = stored.find((entry) => entry.fieldKey === field.key);
    if (!held) continue;
    switch (field.type) {
      case 'money':
        state[field.key] = held.number === null ? '' : String(held.number / 100);
        break;
      case 'number':
        state[field.key] = held.number === null ? '' : String(held.number);
        break;
      case 'date':
        // `<input type="date">` wants `YYYY-MM-DD`, never a full ISO timestamp.
        state[field.key] = held.date ? held.date.slice(0, 10) : '';
        break;
      case 'boolean':
        state[field.key] = held.checked ? 'yes' : '';
        break;
      default:
        state[field.key] = held.text ?? '';
    }
  }
  return state;
}

/**
 * Turn what was typed into the typed slot each field's type uses. A value that will not convert
 * is left out rather than sent as a guess — the shared schema is what reports it, not this.
 */
export function customValuesPayload(
  state: Record<string, string>,
  fields: readonly ResolvedCustomField[],
): CustomFieldValueInput[] {
  const out: CustomFieldValueInput[] = [];

  for (const field of fields) {
    if (field.origin !== 'custom' || field.type === null || field.hidden) continue;
    const raw = (state[field.key] ?? '').trim();

    if (field.type === 'boolean') {
      // A checkbox is never "not given" — it is ticked or it is not.
      out.push({ fieldKey: field.key, checked: raw === 'yes' });
      continue;
    }
    if (raw === '') continue;

    const slot = CUSTOM_FIELD_SLOT[field.type];
    if (slot === 'number') {
      let parsed: number | null = null;
      if (field.type === 'money') {
        try {
          parsed = parseRupees(raw);
        } catch {
          parsed = null;
        }
      } else {
        const asNumber = Number(raw);
        parsed = Number.isFinite(asNumber) ? asNumber : null;
      }
      if (parsed !== null) out.push({ fieldKey: field.key, number: parsed });
      continue;
    }
    if (slot === 'date') {
      // A date input gives a local day; the contract wants an instant.
      out.push({ fieldKey: field.key, date: new Date(`${raw}T00:00:00`).toISOString() });
      continue;
    }
    out.push({ fieldKey: field.key, text: raw });
  }

  return out;
}
